import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Activity, Coins, Image as ImageIcon, PenLine, RefreshCw } from 'lucide-react';

import {
  FLAG_LABELS,
  processorName,
  signalName,
  type SignalDefinitionRow,
  useSignalsOverviewQuery,
  useToggleSignalDefinitionMutation,
} from '@/client/api/assets/signals';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import StatsGrid, { Stat } from '@/client/components/common/StatsGrid';
import JobLauncherDialog from '@/client/components/admin/Assets/JobLauncherDialog';
import SignalVersionsPanel from '@/client/components/admin/Assets/SignalVersionsPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : 'never');

/** Ten bars, 0–9 on the left … 90–100 on the right (100 = clean/good). */
const Histogram = ({ buckets }: { buckets: number[] | null }) => {
  if (!buckets) return <span className="text-xs text-gray-400">—</span>;
  const max = Math.max(...buckets, 1);
  return (
    <div className="flex h-6 items-end gap-px" title={buckets.map((n, i) => `${i * 10}–${i * 10 + 9}: ${n}`).join('\n')}>
      {buckets.map((n, i) => (
        <div
          key={i}
          className={cn('w-1.5 rounded-sm', i < 7 ? 'bg-amber-400' : 'bg-emerald-400')}
          style={{ height: `${Math.max((n / max) * 100, n ? 6 : 2)}%`, opacity: n ? 1 : 0.25 }}
        />
      ))}
    </div>
  );
};

/** Label counts; each links to the library filtered to those assets. */
const LabelChips = ({ def }: { def: SignalDefinitionRow }) => {
  const entries = Object.entries(def.labels).sort((a, b) => b[1] - a[1]);
  // Palette "labels" are hex colours, one per photo: not useful as chips.
  if (!entries.length || entries[0][0].startsWith('#')) return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.slice(0, 5).map(([label, n]) => (
        <Link
          key={label}
          to={`/admin/assets?signal=${encodeURIComponent(`${def.signal_key}:${label}`)}`}
          className={cn(
            'rounded-full px-2 py-0.5 text-[11px] font-medium hover:underline',
            FLAG_LABELS.has(label) ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-600',
          )}
        >
          {label} {n.toLocaleString()}
        </Link>
      ))}
    </div>
  );
};

export const AIAnalysisPage = () => {
  useBreadcrumbs([{ label: 'Asset Management', href: '/admin/assets' }, { label: 'Signals' }]);
  const { data, isLoading, isError, error, refetch, isFetching } = useSignalsOverviewQuery();
  const { hasPermission: canManage, isSuperAdmin } = useHasPermission('asset.signals.manage');
  const toggle = useToggleSignalDefinitionMutation();
  const [pending, setPending] = useState<SignalDefinitionRow | null>(null);
  const [reason, setReason] = useState('');
  const [backfill, setBackfill] = useState<string | null>(null);
  const [shadow, setShadow] = useState<string | null>(null);
  const { hasPermission: canRun } = useHasPermission('asset.run');

  const eligible = data?.eligible_images ?? 0;
  const groups = useMemo(() => {
    const out = new Map<string, SignalDefinitionRow[]>();
    (data?.definitions ?? []).forEach((d) => out.set(d.processor, [...(out.get(d.processor) ?? []), d]));
    return Array.from(out.entries());
  }, [data]);

  const coverageOf = (processor: string) =>
    Math.max(0, ...(data?.definitions ?? []).filter((d) => d.processor === processor).map((d) => d.coverage));
  const overrides = (data?.definitions ?? []).reduce((n, d) => n + d.overridden, 0);

  const stats: Stat[] = [
    { key: 'eligible', label: 'Live images', value: eligible, hint: 'active, not deleted, not identity media', icon: ImageIcon, tone: 'bg-blue-50 text-blue-600' },
    { key: 'vision', label: 'Analysed by vision', value: coverageOf('task:asset-vision'), hint: `${pct(coverageOf('task:asset-vision'), eligible)} of live images · paid`, icon: Coins, tone: 'bg-purple-50 text-purple-600' },
    { key: 'local', label: 'Local metrics', value: coverageOf('task:asset-local-metrics'), hint: `${pct(coverageOf('task:asset-local-metrics'), eligible)} of live images · free`, icon: Activity, tone: 'bg-emerald-50 text-emerald-600' },
    { key: 'overrides', label: 'Reviewer overrides', value: overrides, icon: PenLine, tone: 'bg-amber-50 text-amber-600' },
  ];

  const confirmToggle = async () => {
    const def = pending;
    if (!def || !reason.trim()) return;
    try {
      await toggle.mutateAsync({ sdid: def.sdid, active: def.active !== 1, reason: reason.trim() });
      toast.success(`${signalName(def.signal_key)} ${def.active === 1 ? 'switched off' : 'switched on'}`);
      setPending(null);
      setReason('');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not change the signal'));
    }
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Signals</h1>
            <p className="mt-1 max-w-3xl text-sm text-gray-500">
              What image analysis knows about every photo. Click a label to see the photos behind it; open a photo in the
              library to re-run analysis or override a value.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')} /> Refresh
          </Button>
        </div>

        <StatsGrid stats={stats} isLoading={isLoading && !data} error={isError ? getApiErrorMessage(error, 'Could not load signals.') : null} className="lg:grid-cols-4 xl:grid-cols-4" />

        {isLoading && !data ? (
          <Skeleton className="h-96 w-full" />
        ) : (
          groups.map(([processor, defs]) => {
            const head = defs[0];
            return (
              <section key={processor} className="overflow-hidden rounded-lg border bg-white">
                <div className="flex flex-wrap items-center gap-3 border-b bg-gray-50 px-4 py-3">
                  <h2 className="font-semibold text-gray-900">{processorName(processor)}</h2>
                  <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', head.paid ? 'bg-purple-50 text-purple-700' : 'bg-emerald-50 text-emerald-700')}>
                    {head.paid ? 'Paid per image' : 'Free'}
                  </span>
                  <span className="text-xs text-gray-500">version {head.version}</span>
                  {(canRun || isSuperAdmin) && ['task:asset-local-metrics', 'task:asset-vision'].includes(processor) && (
                    <Button size="sm" variant="outline" className="h-7" onClick={() => setBackfill(processor)}>Backfill…</Button>
                  )}
                  <span className="ml-auto text-xs text-gray-500">
                    last value written {when(defs.map((d) => d.last_computed).filter(Boolean).sort().pop() ?? null)}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs text-gray-500">
                      <tr>
                        <th className="px-4 py-2">Signal</th>
                        <th className="px-4 py-2">Category</th>
                        <th className="px-4 py-2 text-right">Coverage</th>
                        <th className="px-4 py-2">Labels</th>
                        <th className="px-4 py-2">Scores 0→100</th>
                        <th className="px-4 py-2 text-right">Overrides</th>
                        <th className="px-4 py-2">State</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {defs.map((d) => (
                        <tr key={d.sdid} className={cn(d.active !== 1 && 'text-gray-400')}>
                          <td className="px-4 py-2 font-medium">
                            <Link to={`/admin/assets?signal=${encodeURIComponent(d.signal_key)}`} className="hover:underline">
                              {signalName(d.signal_key)}
                            </Link>
                          </td>
                          <td className="px-4 py-2 text-xs">{d.category.replace(/_/g, ' ')}</td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {d.coverage.toLocaleString()}
                            <span className="ml-1 text-xs text-gray-400">{pct(d.coverage, eligible)}</span>
                          </td>
                          <td className="px-4 py-2"><LabelChips def={d} /></td>
                          <td className="px-4 py-2"><Histogram buckets={d.histogram} /></td>
                          <td className="px-4 py-2 text-right tabular-nums">{d.overridden || '—'}</td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', d.active === 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                                {d.active === 1 ? 'On' : 'Off'}
                              </span>
                              {(canManage || isSuperAdmin) && (
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setPending(d)}>
                                  {d.active === 1 ? 'Switch off' : 'Switch on'}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <SignalVersionsPanel processor={processor} onShadowRun={() => setShadow(processor)} />
              </section>
            );
          })
        )}

        {data && (
          <section className="rounded-lg border bg-white p-4">
            <h2 className="mb-1 font-semibold text-gray-900">Categories</h2>
            <p className="mb-3 text-xs text-gray-500">
              How signals roll up into a listing's scores. A listing passes a category at or above its threshold.
            </p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {data.categories.map((c) => (
                <div key={c.scid} className={cn('rounded-md border px-3 py-2 text-sm', c.active !== 1 && 'opacity-50')}>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-gray-500">
                    pass at {c.threshold_pass ?? '—'} · {c.agg_strategy.replace(/_/g, ' ')} {c.active !== 1 && '· off'}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <JobLauncherDialog
        open={!!backfill}
        onClose={() => setBackfill(null)}
        scope={{ filter: { type: 'image', state: 'active' } }}
        scopeLabel="every live image"
        initial={backfill ? { kind: 'signal', processors: [backfill] } : undefined}
      />

      <JobLauncherDialog
        open={!!shadow}
        onClose={() => setShadow(null)}
        scope={{ filter: { type: 'image', state: 'active' } }}
        scopeLabel="every live image (shadow: values go to history, not to the site)"
        initial={shadow ? { kind: 'signal', processors: [shadow], mode: 'shadow' } : undefined}
      />

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.active === 1 ? 'Switch off' : 'Switch on'} {pending ? signalName(pending.signal_key) : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.active === 1
                ? 'New uploads stop producing this signal. Existing values stay.'
                : pending?.paid
                  ? 'New uploads start producing this signal. It is part of a paid processor, so this can add cost per upload.'
                  : 'New uploads start producing this signal.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input placeholder="Reason (required, kept in the audit log)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!reason.trim() || toggle.isPending}
              onClick={(e) => {
                e.preventDefault();
                void confirmToggle();
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
